import { apiBaseUrl } from '../../util/api';
import { isLocalhost } from '../../util/urlHelpers';
import { isSocialErrorResponse } from '../../util/socialResponse';

/**
 * The reviewer's "Foto ersetzen" calls (server/services/social/reviewPhoto.js).
 *
 * They live here, not in util/api.js: that file is shared marketplace code the
 * socials work does not edit (`hd check`). The contract is the one api.js's
 * timedSocialFetch keeps for every other social call — ONE deadline over headers
 * AND body, the server's German message carrying `.status` and `.code`, and the
 * German timeout text — so a stalled response can never pin a busy flag on.
 *
 * Every budget outlasts the server deadline behind it:
 * - options: the gallery read is cut at 15 s on the server;
 * - stage:   55 s on the server, counted from when the upload has ARRIVED — the
 *   upload itself is only bounded by its progress (a slow line is not a timeout);
 * - render:  55 s on the server (the same budget as a plain crop save).
 */
export const PHOTO_OPTIONS_TIMEOUT_MS = 25000;
export const PHOTO_STAGE_TIMEOUT_MS = 70000;
export const PHOTO_UPLOAD_STALL_MS = 30000;
export const PHOTO_RENDER_TIMEOUT_MS = 60000;

const TIMEOUT_MESSAGE = 'Zeitüberschreitung — der Server hat nicht geantwortet.';
export const OFFLINE_MESSAGE =
    'Keine Verbindung zum Server. Bitte die Verbindung prüfen und nochmals versuchen.';
const UNEXPECTED_MESSAGE = 'Der Server hat unerwartet geantwortet. Bitte nochmals versuchen.';

const credentials = () => (isLocalhost() ? 'omit' : 'include');

export const BUSY_MESSAGE =
    'Der Server ist gerade ausgelastet. Bitte in ein paar Minuten erneut versuchen.';

/**
 * The error for an answer that is not ours. DigitalOcean's ingress replaces every
 * 5xx the server sends with its own 504 page: the server's German message is lost,
 * and its status survives only in `x-do-orig-status`.
 * @param {number} status
 * @param {string|null} originalStatus the `x-do-orig-status` header
 * @param {string} fallback
 */
const unexplainedError = (status, originalStatus, fallback) => {
    const original = Number(originalStatus) || status;
    const error = new Error(
        original === 503 || original === 429
            ? BUSY_MESSAGE
            : original >= 500
            ? `Der Server konnte das gerade nicht ausführen (Fehler ${original}). Bitte nochmals versuchen.`
            : fallback
    );
    error.status = original;
    error.code = null;
    return error;
};

const timedFetch = async (path, options, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(`${apiBaseUrl()}${path}`, {
            ...options,
            credentials: credentials(),
            signal: controller.signal,
        });
        let json;
        try {
            json = await res.json();
        } catch (parseError) {
            if (parseError && parseError.name === 'AbortError') throw parseError;
            json = {};
        }
        if (isSocialErrorResponse(res.status, json)) {
            if (!json.message) {
                throw unexplainedError(
                    res.status,
                    res.headers.get('x-do-orig-status'),
                    `Die Anfrage ist fehlgeschlagen (${res.status}).`
                );
            }
            const error = new Error(json.message);
            error.status = res.status;
            error.code = json.code || null;
            throw error;
        }
        return json;
    } catch (e) {
        if (e && e.name === 'AbortError') throw new Error(TIMEOUT_MESSAGE);
        // fetch's own failure (no connection, blocked) arrives as a TypeError in
        // the browser's language ("Failed to fetch").
        if (e instanceof TypeError) throw new Error(OFFLINE_MESSAGE);
        throw e;
    } finally {
        clearTimeout(timer);
    }
};

/**
 * The server's stream of a gallery photo — the fallback when the browser may not
 * read it from S3/Sharetribe directly (photoPrepare.loadGalleryPhoto).
 */
export const photoSourceRequest = (postId, url) => ({
    href: `${apiBaseUrl()}/api/social-posts/${postId}/photo-source?url=${encodeURIComponent(url)}`,
    init: { credentials: credentials() },
});

/** The profile gallery a post can switch to. */
export const fetchPhotoOptions = postId =>
    timedFetch(
        `/api/social-posts/${postId}/photo-options`,
        { method: 'GET' },
        PHOTO_OPTIONS_TIMEOUT_MS
    );

const STALLED_MESSAGE = 'Das Hochladen kommt nicht voran. Bitte die Verbindung prüfen.';

/**
 * Stage a photo the browser prepared (photoPrepare.js): `kind` 'upload' or
 * 'gallery' (+ its `galleryUrl`). Resolves to `{ ref, token, url }`; the post
 * changes only when the crop is saved. XHR, not fetch, for the upload's progress:
 * `onUploadProgress({ loaded, total })`. Aborts with `signal`.
 */
export const stagePhoto = (postId, { blob, kind, galleryUrl }, { onUploadProgress, signal } = {}) =>
    new Promise((resolve, reject) => {
        const body = new FormData();
        body.append('kind', kind);
        if (galleryUrl) body.append('galleryUrl', galleryUrl);
        body.append('file', blob, 'foto.jpg');
        const xhr = new XMLHttpRequest();
        let timer = null;
        const arm = (ms, message) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                xhr.abort();
                reject(new Error(message));
            }, ms);
        };
        const finish = () => {
            clearTimeout(timer);
            if (signal) signal.removeEventListener('abort', onAbort);
        };
        const onAbort = () => {
            finish();
            xhr.abort();
            const error = new Error('Abgebrochen.');
            error.name = 'AbortError';
            reject(error);
        };
        if (signal) {
            if (signal.aborted) {
                onAbort();
                return;
            }
            signal.addEventListener('abort', onAbort);
        }
        xhr.open('POST', `${apiBaseUrl()}/api/social-posts/${postId}/photos`);
        xhr.withCredentials = credentials() === 'include';
        xhr.responseType = 'text';
        xhr.upload.onprogress = e => {
            arm(PHOTO_UPLOAD_STALL_MS, STALLED_MESSAGE);
            if (onUploadProgress && e.lengthComputable) {
                onUploadProgress({ loaded: e.loaded, total: e.total });
            }
        };
        // Sent: from here on only the server's own deadline runs.
        xhr.upload.onload = () => arm(PHOTO_STAGE_TIMEOUT_MS, TIMEOUT_MESSAGE);
        const offline = () => {
            finish();
            reject(
                new Error('Das Foto konnte nicht gesendet werden. Bitte die Verbindung prüfen.')
            );
        };
        xhr.onerror = offline;
        xhr.onload = () => {
            if (xhr.status === 0) {
                offline();
                return;
            }
            finish();
            let json = {};
            try {
                json = JSON.parse(xhr.responseText || '{}');
            } catch (_) {
                json = {};
            }
            if (isSocialErrorResponse(xhr.status, json)) {
                if (!json.message) {
                    reject(
                        unexplainedError(
                            xhr.status,
                            xhr.getResponseHeader('x-do-orig-status'),
                            `Das Foto wurde nicht angenommen (${xhr.status}).`
                        )
                    );
                    return;
                }
                const error = new Error(json.message);
                error.status = xhr.status;
                error.code = json.code || null;
                reject(error);
                return;
            }
            // Only the staged photo itself is an answer: anything else (a proxy's
            // page, an empty body) would surface later as a crash.
            if (
                !json.ref ||
                typeof json.ref.sha256 !== 'string' ||
                typeof json.token !== 'string'
            ) {
                reject(new Error(UNEXPECTED_MESSAGE));
                return;
            }
            resolve(json);
        };
        arm(PHOTO_UPLOAD_STALL_MS, STALLED_MESSAGE);
        xhr.send(body);
    });

/**
 * The crop save, carrying the photo the channel switches to: a staged one
 * (`{ sha256, kind, token }`, with `from` when it is taken over from another
 * channel) or `{ kind: 'original' }`.
 */
export const rerenderWithPhoto = (postId, platform, doc, photo) =>
    timedFetch(
        `/api/social-posts/${postId}/channels/${encodeURIComponent(platform)}/rerender`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ doc, photo }),
        },
        PHOTO_RENDER_TIMEOUT_MS
    );
